import { useState, useEffect, useCallback } from "react";

interface UseExpandableStateOptions {
  storageKey: string;
  defaultExpanded?: boolean;
  /** Item id to force-expand on first render (e.g. from URL hash), merged with localStorage. */
  forceExpanded?: string;
}

interface UseExpandableStateReturn {
  isExpanded: (id: string) => boolean;
  toggleExpanded: (id: string) => void;
  setExpanded: (id: string, expanded: boolean) => void;
  expandAll: (itemIds: string[]) => void;
  collapseAll: (itemIds: string[]) => void;
  hasAnyExpanded: (itemIds: string[]) => boolean;
}

const readStorage = (key: string): string[] => {
  try {
    const stored = localStorage.getItem(key);
    const parsed = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/**
 * Manages expandable section state with localStorage persistence.
 * localStorage is read synchronously so the first render reflects persisted state immediately.
 */
export const useExpandableState = ({
  storageKey,
  defaultExpanded = false,
  forceExpanded,
}: UseExpandableStateOptions): UseExpandableStateReturn => {
  const [expandedIds, setExpandedIds] = useState<string[]>(() => {
    const stored = readStorage(storageKey);
    return forceExpanded && !stored.includes(forceExpanded) ? [...stored, forceExpanded] : stored;
  });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(expandedIds));
    } catch {
      // ignore storage errors
    }
  }, [expandedIds, storageKey]);

  const isExpanded = useCallback(
    (id: string) => defaultExpanded ? !expandedIds.includes(id) : expandedIds.includes(id),
    [expandedIds, defaultExpanded],
  );

  const setExpanded = useCallback(
    (id: string, expanded: boolean) => {
      const shouldAdd = defaultExpanded ? !expanded : expanded;
      setExpandedIds((prev) =>
        shouldAdd
          ? prev.includes(id) ? prev : [...prev, id]
          : prev.filter((x) => x !== id),
      );
    },
    [defaultExpanded],
  );

  const toggleExpanded = useCallback(
    (id: string) => setExpanded(id, !isExpanded(id)),
    [isExpanded, setExpanded],
  );

  const expandAll = useCallback(
    (ids: string[]) => setExpandedIds(defaultExpanded ? [] : ids),
    [defaultExpanded],
  );

  const collapseAll = useCallback(
    (ids: string[]) => setExpandedIds(defaultExpanded ? ids : []),
    [defaultExpanded],
  );

  const hasAnyExpanded = useCallback(
    (ids: string[]) => ids.some((id) => isExpanded(id)),
    [isExpanded],
  );

  return { isExpanded, toggleExpanded, setExpanded, expandAll, collapseAll, hasAnyExpanded };
};

export default useExpandableState;
